import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { ChequeMultiFormProps, ChequeMultiFormRef } from "./types";
import { Bank, ChequeBuilder, Timestamp } from "../../../types";
import { Column, Row } from "../../containers";
import { AlignItems, JustifyContent } from "../../containers/types";
import { Heading } from "../../typography";
import { useThemeStore } from "../../../stores";
import { ChequeCounter } from "../../../pages/finance/components";
import ChequeForm from "../cheque-form";

const INITIAL_CHEQUE: ChequeBuilder = {
  name: "",
  account_no: "",
  bank: null,
  number: "",
  price: 0,
  shenase: "",
  date: Date.now(),
};

const INITIAL_FILE: (FileList | null)[] = [null];

const ChequeMultiForm = forwardRef<ChequeMultiFormRef, ChequeMultiFormProps>(
  (props, ref) => {
    const { className = "", props: p, style = {} } = props;

    const theme = useThemeStore((s) => s.theme);

    const [nrCheques, setNrCheques] = useState<number>(1);
    const [selectedChequeIdx, setSelectedChequeIdx] = useState<number>(0);

    const [cheques, setCheques] = useState<Array<ChequeBuilder>>([
      { ...INITIAL_CHEQUE },
    ]);
    const [files, setFiles] = useState<(FileList | null)[]>([...INITIAL_FILE]);

    const setChequesValue = useCallback(
      <T extends keyof ChequeBuilder>(field: T, value: ChequeBuilder[T]) => {
        setCheques((prev) => {
          prev[selectedChequeIdx][field] = value;
          return [...prev];
        });
      },
      [selectedChequeIdx]
    );

    const setFilesValue = useCallback(
      (v: FileList | null) => {
        setFiles((prev) => {
          prev[selectedChequeIdx] = v;
          return [...prev];
        });
      },
      [selectedChequeIdx]
    );

    const onNext = useCallback(() => {
      if (selectedChequeIdx === nrCheques - 1) {
        setNrCheques((prev) => prev + 1);
        setCheques((prev) => [...prev, { ...INITIAL_CHEQUE }]);
        setFiles((prev) => [...prev, ...INITIAL_FILE]);
      } else {
        setSelectedChequeIdx((prev) => prev + 1);
      }
    }, [nrCheques, selectedChequeIdx]);

    useEffect(() => {
      setSelectedChequeIdx(nrCheques - 1);
    }, [nrCheques]);

    const validateCheques = useCallback((): boolean => {
      if (cheques.length !== files.length) return false;

      for (let i = 0; i < cheques.length; i++) {
        const c = cheques[i];
        if (!files[i]) return false;
        if (
          !c.account_no ||
          !c.bank ||
          !c.date ||
          !c.name ||
          !c.number ||
          !c.price ||
          !c.shenase
        )
          return false;
      }

      return true;
    }, [cheques, files]);

    const onDelete = useCallback(() => {
      if (nrCheques === 1) {
        setCheques([{ ...INITIAL_CHEQUE }]);
        setFiles([...INITIAL_FILE]);
        return;
      }

      setSelectedChequeIdx(0);

      setCheques((prev) => {
        return prev.filter((_, idx) => idx !== selectedChequeIdx);
      });

      setFiles((prev) => {
        return prev.filter((_, idx) => idx !== selectedChequeIdx);
      });

      setNrCheques((prev) => prev - 1);
    }, [selectedChequeIdx, nrCheques]);

    useImperativeHandle(ref, () => ({
      getCheques: () => cheques,
      getFiles: () => files,
      isValid: () => validateCheques(),
    }));

    return (
      <Column
        alignItems={AlignItems.Start}
        gap={10}
        className={className}
        props={p}
        style={style}
      >
        <Row style={{ width: "100%" }} justifyContent={JustifyContent.Between}>
          <Heading
            size={20}
            color={theme.textOnOverlay}
            textStyles={{ width: "100%" }}
          >
            مشخصات چک
          </Heading>
          <ChequeCounter
            nrCheques={nrCheques}
            selectedChequeIdx={selectedChequeIdx}
            setSelectedChequeIdx={setSelectedChequeIdx}
          />
        </Row>
        <ChequeForm
          account_no={cheques[selectedChequeIdx].account_no}
          bank={cheques[selectedChequeIdx].bank}
          date={cheques[selectedChequeIdx].date}
          file={files[selectedChequeIdx]}
          name={cheques[selectedChequeIdx].name}
          number={cheques[selectedChequeIdx].number}
          price={cheques[selectedChequeIdx].price}
          shenase={cheques[selectedChequeIdx].shenase}
          onDelete={onDelete}
          onNext={onNext}
          setAccountNo={(v) => setChequesValue("account_no", v as string)}
          setBank={(v) => setChequesValue("bank", v as Bank | null)}
          setDate={(v) => setChequesValue("date", v as Timestamp)}
          setName={(v) => setChequesValue("name", v as string)}
          setNumber={(v) => setChequesValue("number", v as string)}
          setPrice={(v) => setChequesValue("price", v as number)}
          setShenase={(v) => setChequesValue("shenase", v as string)}
          setFile={setFilesValue}
          buttons={["delete", "next"]}
        />
      </Column>
    );
  }
);

export default ChequeMultiForm;
